// src/pages/inspector/InspectorSchedule.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InspectorLayout from '../../components/inspector/InspectorLayout';
import inspectorAPI from '../../services/inspectorAPI';
import { StatusBadge } from './InspectorDashboard';
import styles from './InspectorSchedule.module.css';

const TIME_LABELS = { morning: '9AM–12PM', afternoon: '12PM–4PM', evening: '4PM–7PM' };

function buildWeek(baseDate) {
  const start = new Date(baseDate);
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function toDateStr(date) {
  return date.toISOString().split('T')[0];
}

export default function InspectorSchedule() {
  const [inspections, setInspections] = useState([]);
  const [weekBase, setWeekBase] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await inspectorAPI.getInspections();
        setInspections(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const week = buildWeek(weekBase);
  const today = toDateStr(new Date());

  const prevWeek = () => {
    const d = new Date(weekBase);
    d.setDate(d.getDate() - 7);
    setWeekBase(d);
  };

  const nextWeek = () => {
    const d = new Date(weekBase);
    d.setDate(d.getDate() + 7);
    setWeekBase(d);
  };

  const getForDay = (date) => {
    const ds = toDateStr(date);
    return inspections.filter((i) => i.preferred_date === ds);
  };

  return (
    <InspectorLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Schedule</h1>
            <p className={styles.subtitle}>Weekly inspection calendar</p>
          </div>
          <div className={styles.weekNav}>
            <button onClick={prevWeek}>← Prev</button>
            <span className={styles.weekLabel}>
              {week[0].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} –{' '}
              {week[6].toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <button onClick={nextWeek}>Next →</button>
          </div>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading schedule…</div>
        ) : (
          <div className={styles.calendar}>
            {week.map((date) => {
              const ds = toDateStr(date);
              const isToday = ds === today;
              const dayItems = getForDay(date);
              const dayName = date.toLocaleDateString('en-IN', { weekday: 'short' });
              const dayNum = date.getDate();

              return (
                <div
                  key={ds}
                  className={`${styles.dayCol} ${isToday ? styles.today : ''}`}
                >
                  <div className={styles.dayHeader}>
                    <span className={styles.dayName}>{dayName}</span>
                    <span className={`${styles.dayNum} ${isToday ? styles.todayNum : ''}`}>
                      {dayNum}
                    </span>
                    {dayItems.length > 0 && (
                      <span className={styles.dayCount}>{dayItems.length}</span>
                    )}
                  </div>
                  <div className={styles.dayBody}>
                    {dayItems.length === 0 ? (
                      <div className={styles.freeDay}>Free</div>
                    ) : (
                      dayItems.map((ins) => (
                        <Link
                          key={ins.id}
                          to={`/inspector/inspections/${ins.id}`}
                          className={styles.eventCard}
                        >
                          <div className={styles.eventTime}>
                            {TIME_LABELS[ins.preferred_time] || ins.preferred_time}
                          </div>
                          <div className={styles.eventCar}>
                            {ins.car.brand} {ins.car.model}
                          </div>
                          <div className={styles.eventCity}>{ins.city}</div>
                          <StatusBadge status={ins.status} />
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Today's list */}
        {!loading && (
          <div className={styles.todaySection}>
            <h2 className={styles.sectionTitle}>Today's Inspections</h2>
            {getForDay(new Date()).length === 0 ? (
              <p className={styles.noToday}>No inspections scheduled for today.</p>
            ) : (
              <div className={styles.todayList}>
                {getForDay(new Date()).map((ins) => (
                  <Link
                    key={ins.id}
                    to={`/inspector/inspections/${ins.id}`}
                    className={styles.todayCard}
                  >
                    <div className={styles.todayTime}>
                      {TIME_LABELS[ins.preferred_time] || ins.preferred_time}
                    </div>
                    <div className={styles.todayMain}>
                      <div className={styles.todayCar}>
                        {ins.car.brand} {ins.car.model} ({ins.car.year})
                      </div>
                      <div className={styles.todayMeta}>
                        📍 {ins.city} · 🏷️ {ins.booking_reference}
                      </div>
                    </div>
                    <StatusBadge status={ins.status} />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </InspectorLayout>
  );
}